import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ROUTE_PATH } from '../constants/routes';

export interface FaqItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

export interface FaqCategory {
  id: string;
  title: string;
  icon?: string;
  items: FaqItem[];
}

export const useFaqData = () => {
  const { t } = useTranslation();

  const faqCategories: FaqCategory[] = useMemo(
    () => [
      {
        id: 'reservations',
        title: t('faq.categories.reservations'),
        items: [
          {
            id: 'how-to-reserve',
            question: t('faq.reservations.howToReserve.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.reservations.howToReserve.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">1.</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.howToReserve.step1')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">2.</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.howToReserve.step2')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">3.</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.howToReserve.step3')}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'verification-email',
            question: t('faq.reservations.verificationEmail.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.reservations.verificationEmail.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">-</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.verificationEmail.reason1')}
                  </div>
                </div>
                <div className="faq-row">
                  <div className="faq-row__label">-</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.verificationEmail.reason2')}
                  </div>
                </div>
                <div className="faq-row">
                  <div className="faq-row__label">-</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.verificationEmail.reason3')}
                  </div>
                </div>
                <p className="faq__text l-mt20">
                  {t('faq.reservations.verificationEmail.solution')}
                </p>
              </div>
            ),
          },
          {
            id: 'cancel-change',
            question: t('faq.reservations.cancelChange.question'),
            answer: (
              <p className="faq__text">
                {t('faq.reservations.cancelChange.answer')}
              </p>
            ),
          },
          {
            id: 'wrong-terminal',
            question: t('faq.reservations.wrongTerminal.question'),
            answer: (
              <p className="faq__text">
                {t('faq.reservations.wrongTerminal.answer')}
              </p>
            ),
          },
          {
            id: 'check-reservation',
            question: t('faq.reservations.checkReservation.question'),
            answer: (
              <p className="faq__text">
                {t('faq.reservations.checkReservation.answer')}
              </p>
            ),
          },
          {
            id: 'time-not-enough',
            question: t('faq.reservations.timeNotEnough.question'),
            answer: (
              <p className="faq__text">
                {t('faq.reservations.timeNotEnough.answer')}
              </p>
            ),
          },
          {
            id: 'multiple-reservations',
            question: t('faq.reservations.multipleReservations.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.reservations.multipleReservations.answer')}
                </p>
                <p className="faq__text l-mt20">
                  {t('faq.reservations.multipleReservations.answerBooth')}
                </p>
                <p className="faq__text l-mt20">
                  {t('faq.reservations.multipleReservations.boothLimit')}
                </p>
                <div className="faq-row">
                  <div className="faq-row__label">*</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.multipleReservations.note')}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'extend-time',
            question: t('faq.reservations.extendTime.question'),
            answer: (
              <p className="faq__text">
                {t('faq.reservations.extendTime.answer')}
              </p>
            ),
          },
          {
            id: 'reservation-process',
            question: t('faq.reservations.reservationProcess.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.reservations.reservationProcess.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">1.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.step1Main')}
                    </p>
                    <p className="faq__text l-mt10">
                      {t('faq.reservations.reservationProcess.step1Supplement')}
                    </p>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">・</div>
                      <div className="faq-row__body">
                        {t(
                          'faq.reservations.reservationProcess.availabilityIntl',
                        )}
                      </div>
                    </div>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">・</div>
                      <div className="faq-row__body">
                        {t(
                          'faq.reservations.reservationProcess.availabilityDomestic',
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">2.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.step2')}
                    </p>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">3.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.step3Main')}
                    </p>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">・</div>
                      <div className="faq-row__body">
                        {t(
                          'faq.reservations.reservationProcess.entryTimingIntl',
                        )}
                      </div>
                    </div>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">・</div>
                      <div className="faq-row__body">
                        {t(
                          'faq.reservations.reservationProcess.entryTimingDomestic',
                        )}
                      </div>
                    </div>
                    <p className="faq__text l-mt10">
                      {t('faq.reservations.reservationProcess.step3Supplement')}
                    </p>
                  </div>
                </div>
                <p className="faq__text l-mt20">
                  {t('faq.reservations.reservationProcess.entryTitle')}
                </p>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.reservationProcess.entryIntlTitle')}
                    <br />
                    {t('faq.reservations.reservationProcess.entryIntlSubtitle')}
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.entryIntlPasscodeTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.entryIntlPasscodeDetail',
                      )}
                    </p>
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.entryIntlKeyTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.entryIntlKeyDetail',
                      )}
                    </p>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t(
                      'faq.reservations.reservationProcess.entryDomesticTitle',
                    )}
                    <br />
                    {t(
                      'faq.reservations.reservationProcess.entryDomesticDetail',
                    )}
                    <br />
                    {t(
                      'faq.reservations.reservationProcess.entryDomesticNote',
                    )}
                  </div>
                </div>
                <p className="faq__text l-mt20">
                  {t('faq.reservations.reservationProcess.exitTitle')}
                </p>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.reservationProcess.exitIntlTitle')}
                    <br />
                    {t('faq.reservations.reservationProcess.exitIntlSubtitle')}
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.exitIntlPasscodeTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.exitIntlPasscodeDetail',
                      )}
                    </p>
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.exitIntlKeyTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.exitIntlKeyDetail',
                      )}
                    </p>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.reservationProcess.exitDomesticTitle')}
                    <br />
                    {t(
                      'faq.reservations.reservationProcess.exitDomesticDetail',
                    )}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'personal-info',
            question: t('faq.reservations.personalInfo.question'),
            answer: (
              <p className="faq__text">
                {t('faq.reservations.personalInfo.answer')}
              </p>
            ),
          },
        ],
      },
      {
        id: 'day-of-use',
        title: t('faq.categories.dayOfUse'),
        items: [
          {
            id: 'check-reservation-day',
            question: t('faq.dayOfUse.checkReservation.question'),
            answer: (
              <p className="faq__text">
                {t('faq.dayOfUse.checkReservation.answer')}
              </p>
            ),
          },
          {
            id: 'enter-exit',
            question: t('faq.dayOfUse.enterExit.question'),
            answer: (
              <p className="faq__text">{t('faq.dayOfUse.enterExit.answer')}</p>
            ),
          },
          {
            id: 'extend-time-day',
            question: t('faq.dayOfUse.extendTime.question'),
            answer: (
              <p className="faq__text">{t('faq.dayOfUse.extendTime.answer')}</p>
            ),
          },
          {
            id: 'how-to-enter',
            question: t('faq.dayOfUse.howToEnter.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.dayOfUse.howToEnter.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.intlShowerTitle')}
                    <br />
                    {t('faq.dayOfUse.howToEnter.intlShowerSubtitle')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">■</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.passcodeTitle')}
                    <br />
                    {t('faq.dayOfUse.howToEnter.passcodeDetail')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.blueFlash')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.greenFlash')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.redFlash')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.unlockTiming')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">※</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.touchSensor')}
                  </div>
                </div>
                <div className="faq-row">
                  <div className="faq-row__label">※</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.cancelInput')}
                  </div>
                </div>
                <div className="faq-row">
                  <div className="faq-row__label">※</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.passcodeLocation')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">■</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.keyTitle')}
                    <br />
                    {t('faq.dayOfUse.howToEnter.keyDetail')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.dayOfUse.howToEnter.domesticBoothTitle')}
                    </p>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">1.</div>
                      <div className="faq-row__body">
                        {t('faq.dayOfUse.howToEnter.domesticBoothStep1')}
                      </div>
                    </div>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">2.</div>
                      <div className="faq-row__body">
                        {t('faq.dayOfUse.howToEnter.domesticBoothStep2')}
                      </div>
                    </div>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">3.</div>
                      <div className="faq-row__body">
                        {t('faq.dayOfUse.howToEnter.domesticBoothStep3')}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">※</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.domesticBoothNote1')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">※</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.domesticBoothNote2')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">※</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToEnter.domesticBoothNote3')}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'how-to-exit',
            question: t('faq.dayOfUse.howToExit.question'),
            answer: (
              <div>
                <p className="faq__text">{t('faq.dayOfUse.howToExit.intro')}</p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToExit.intlShowerTitle')}
                    <br />
                    {t('faq.dayOfUse.howToExit.intlShowerSubtitle')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">■</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToExit.passcodeTitle')}
                    <br />
                    {t('faq.dayOfUse.howToExit.passcodeDetail')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">■</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToExit.keyTitle')}
                    <br />
                    {t('faq.dayOfUse.howToExit.keyDetail')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.howToExit.domesticBoothTitle')}
                    <br />
                    {t('faq.dayOfUse.howToExit.domesticBoothDetail')}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'lock-not-opening',
            question: t('faq.dayOfUse.lockNotOpening.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.dayOfUse.lockNotOpening.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">1.</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.lockNotOpening.step1')}
                  </div>
                </div>
                <div className="l-mt20">
                  <video
                    src={t('faq.dayOfUse.lockNotOpening.videoSrc')}
                    controls
                    preload="metadata"
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">2.</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.lockNotOpening.step2')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">3.</div>
                  <div className="faq-row__body">
                    {t('faq.dayOfUse.lockNotOpening.step3')}
                  </div>
                </div>
                <div className="l-mt20">
                  <video
                    src={t('faq.dayOfUse.lockNotOpening.videoSrc2')}
                    controls
                    preload="metadata"
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                </div>
              </div>
            ),
          },
          {
            id: 'reservation-process-day',
            question: t('faq.reservations.reservationProcess.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.reservations.reservationProcess.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">1.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.step1Main')}
                    </p>
                    <p className="faq__text l-mt10">
                      {t('faq.reservations.reservationProcess.step1Supplement')}
                    </p>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">・</div>
                      <div className="faq-row__body">
                        {t(
                          'faq.reservations.reservationProcess.availabilityIntl',
                        )}
                      </div>
                    </div>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">・</div>
                      <div className="faq-row__body">
                        {t(
                          'faq.reservations.reservationProcess.availabilityDomestic',
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">2.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.step2')}
                    </p>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">3.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.step3Main')}
                    </p>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">・</div>
                      <div className="faq-row__body">
                        {t(
                          'faq.reservations.reservationProcess.entryTimingIntl',
                        )}
                      </div>
                    </div>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">・</div>
                      <div className="faq-row__body">
                        {t(
                          'faq.reservations.reservationProcess.entryTimingDomestic',
                        )}
                      </div>
                    </div>
                    <p className="faq__text l-mt10">
                      {t('faq.reservations.reservationProcess.step3Supplement')}
                    </p>
                  </div>
                </div>
                <p className="faq__text l-mt20">
                  {t('faq.reservations.reservationProcess.entryTitle')}
                </p>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.reservationProcess.entryIntlTitle')}
                    <br />
                    {t('faq.reservations.reservationProcess.entryIntlSubtitle')}
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.entryIntlPasscodeTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.entryIntlPasscodeDetail',
                      )}
                    </p>
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.entryIntlKeyTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.entryIntlKeyDetail',
                      )}
                    </p>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t(
                      'faq.reservations.reservationProcess.entryDomesticTitle',
                    )}
                    <br />
                    {t(
                      'faq.reservations.reservationProcess.entryDomesticDetail',
                    )}
                    <br />
                    {t(
                      'faq.reservations.reservationProcess.entryDomesticNote',
                    )}
                  </div>
                </div>
                <p className="faq__text l-mt20">
                  {t('faq.reservations.reservationProcess.exitTitle')}
                </p>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.reservationProcess.exitIntlTitle')}
                    <br />
                    {t('faq.reservations.reservationProcess.exitIntlSubtitle')}
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.exitIntlPasscodeTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.exitIntlPasscodeDetail',
                      )}
                    </p>
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.exitIntlKeyTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.exitIntlKeyDetail',
                      )}
                    </p>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.reservationProcess.exitDomesticTitle')}
                    <br />
                    {t(
                      'faq.reservations.reservationProcess.exitDomesticDetail',
                    )}
                  </div>
                </div>
              </div>
            ),
          },
        ],
      },
      {
        id: 'shower-room',
        title: t('faq.categories.showerRoom'),
        icon: '/D_1_Shower.png',
        items: [
          {
            id: 'shower-how-to-reserve',
            question: t('faq.showerRoom.howToReserve.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.showerRoom.howToReserve.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">1.</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToReserve.step1')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">2.</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToReserve.step2')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">3.</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToReserve.step3')}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'shower-cancel-change',
            question: t('faq.showerRoom.cancelChange.question'),
            answer: (
              <p className="faq__text">
                {t('faq.showerRoom.cancelChange.answer')}
              </p>
            ),
          },
          {
            id: 'shower-wrong-terminal',
            question: t('faq.showerRoom.wrongTerminal.question'),
            answer: (
              <p className="faq__text">
                {t('faq.showerRoom.wrongTerminal.answer')}
              </p>
            ),
          },
          {
            id: 'shower-check-reservation',
            question: t('faq.showerRoom.checkReservation.question'),
            answer: (
              <p className="faq__text">
                {t('faq.showerRoom.checkReservation.answer')}
              </p>
            ),
          },
          {
            id: 'shower-time-not-enough',
            question: t('faq.showerRoom.timeNotEnough.question'),
            answer: (
              <p className="faq__text">
                {t('faq.showerRoom.timeNotEnough.answer')}
              </p>
            ),
          },
          {
            id: 'shower-multiple-reservations',
            question: t('faq.showerRoom.multipleReservations.question'),
            answer: (
              <p className="faq__text">
                {t('faq.showerRoom.multipleReservations.answer')}
              </p>
            ),
          },
          {
            id: 'shower-companion',
            question: t('faq.showerRoom.companion.question'),
            answer: (
              <p className="faq__text">
                {t('faq.showerRoom.companion.answer')}
              </p>
            ),
          },
          {
            id: 'shower-enter-exit',
            question: t('faq.showerRoom.enterExit.question'),
            answer: (
              <p className="faq__text">
                {t('faq.showerRoom.enterExit.answer')}
              </p>
            ),
          },
          {
            id: 'shower-wheelchair',
            question: t('faq.showerRoom.wheelchair.question'),
            answer: (
              <p className="faq__text">
                {t('faq.showerRoom.wheelchair.answer')}
              </p>
            ),
          },
          {
            id: 'shower-extend-time',
            question: t('faq.showerRoom.extendTime.question'),
            answer: (
              <p className="faq__text">
                {t('faq.showerRoom.extendTime.answer')}
              </p>
            ),
          },
          {
            id: 'shower-how-to-enter',
            question: t('faq.showerRoom.howToEnter.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.showerRoom.howToEnter.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToEnter.intlShowerTitle')}
                    <br />
                    {t('faq.showerRoom.howToEnter.intlShowerSubtitle')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">■</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToEnter.passcodeTitle')}
                    <br />
                    {t('faq.showerRoom.howToEnter.passcodeDetail')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToEnter.blueFlash')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToEnter.greenFlash')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToEnter.redFlash')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToEnter.unlockTiming')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">※</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToEnter.touchSensor')}
                  </div>
                </div>
                <div className="faq-row">
                  <div className="faq-row__label">※</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToEnter.cancelInput')}
                  </div>
                </div>
                <div className="faq-row">
                  <div className="faq-row__label">※</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToEnter.passcodeLocation')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">■</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToEnter.keyTitle')}
                    <br />
                    {t('faq.showerRoom.howToEnter.keyDetail')}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'shower-how-to-exit',
            question: t('faq.showerRoom.howToExit.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.showerRoom.howToExit.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToExit.intlShowerTitle')}
                    <br />
                    {t('faq.showerRoom.howToExit.intlShowerSubtitle')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">■</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToExit.passcodeTitle')}
                    <br />
                    {t('faq.showerRoom.howToExit.passcodeDetail')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">■</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.howToExit.keyTitle')}
                    <br />
                    {t('faq.showerRoom.howToExit.keyDetail')}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'shower-lock-not-opening',
            question: t('faq.showerRoom.lockNotOpening.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.showerRoom.lockNotOpening.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">1.</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.lockNotOpening.step1')}
                  </div>
                </div>
                <div className="l-mt20">
                  <video
                    src={t('faq.showerRoom.lockNotOpening.videoSrc')}
                    controls
                    preload="metadata"
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">2.</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.lockNotOpening.step2')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">3.</div>
                  <div className="faq-row__body">
                    {t('faq.showerRoom.lockNotOpening.step3')}
                  </div>
                </div>
                <div className="l-mt20">
                  <video
                    src={t('faq.showerRoom.lockNotOpening.videoSrc2')}
                    controls
                    preload="metadata"
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                </div>
              </div>
            ),
          },
          {
            id: 'shower-reservation-process',
            question: t('faq.reservations.reservationProcess.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.reservations.reservationProcess.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">1.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.step1Main')}
                    </p>
                    <p className="faq__text l-mt10">
                      {t('faq.reservations.reservationProcess.step1Supplement')}
                    </p>
                    <div className="faq-row l-mt10">
                      <div className="faq-row__label">・</div>
                      <div className="faq-row__body">
                        {t(
                          'faq.reservations.reservationProcess.availabilityIntl',
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">2.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.step2')}
                    </p>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">3.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.step3')}
                    </p>
                  </div>
                </div>
                <p className="faq__text l-mt20">
                  {t('faq.reservations.reservationProcess.entryTitle')}
                </p>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.reservationProcess.entryIntlTitle')}
                    <br />
                    {t('faq.reservations.reservationProcess.entryIntlSubtitle')}
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.entryIntlPasscodeTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.entryIntlPasscodeDetail',
                      )}
                    </p>
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.entryIntlKeyTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.entryIntlKeyDetail',
                      )}
                    </p>
                  </div>
                </div>
                <p className="faq__text l-mt20">
                  {t('faq.reservations.reservationProcess.exitTitle')}
                </p>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">・</div>
                  <div className="faq-row__body">
                    {t('faq.reservations.reservationProcess.exitIntlTitle')}
                    <br />
                    {t('faq.reservations.reservationProcess.exitIntlSubtitle')}
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.exitIntlPasscodeTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.exitIntlPasscodeDetail',
                      )}
                    </p>
                    <p className="faq__text l-mt20">
                      {t(
                        'faq.reservations.reservationProcess.exitIntlKeyTitle',
                      )}
                      <br />
                      {t(
                        'faq.reservations.reservationProcess.exitIntlKeyDetail',
                      )}
                    </p>
                  </div>
                </div>
              </div>
            ),
          },
        ],
      },
      {
        id: 'work-booth',
        title: t('faq.categories.workBooth'),
        icon: '/D_1_Booth.png',
        items: [
          {
            id: 'booth-how-to-reserve',
            question: t('faq.workBooth.howToReserve.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.workBooth.howToReserve.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">1.</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.howToReserve.step1')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">2.</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.howToReserve.step2')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">3.</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.howToReserve.step3')}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'booth-cancel-change',
            question: t('faq.workBooth.cancelChange.question'),
            answer: t('faq.workBooth.cancelChange.answer'),
          },
          {
            id: 'booth-check-reservation',
            question: t('faq.workBooth.checkReservation.question'),
            answer: (
              <p className="faq__text">
                {t('faq.workBooth.checkReservation.answer')}
              </p>
            ),
          },
          {
            id: 'booth-multiple-reservations',
            question: t('faq.workBooth.multipleReservations.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.workBooth.multipleReservations.answer')}
                </p>
                <p className="faq__text l-mt20">
                  {t('faq.workBooth.multipleReservations.boothLimit')}
                </p>
                <div className="faq-row">
                  <div className="faq-row__label">*</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.multipleReservations.note')}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'booth-enter-exit',
            question: t('faq.workBooth.enterExit.question'),
            answer: (
              <p className="faq__text">{t('faq.workBooth.enterExit.answer')}</p>
            ),
          },
          {
            id: 'booth-extend-time',
            question: t('faq.workBooth.extendTime.question'),
            answer: (
              <p className="faq__text">
                {t('faq.workBooth.extendTime.answer')}
              </p>
            ),
          },
          {
            id: 'booth-how-to-enter',
            question: t('faq.workBooth.howToEnter.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.workBooth.howToEnter.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">1.</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.howToEnter.step1')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">2.</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.howToEnter.step2')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">3.</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.howToEnter.step3')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">*</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.howToEnter.note1')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">*</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.howToEnter.note2')}
                  </div>
                </div>
                <div className="faq-row l-mt10">
                  <div className="faq-row__label">*</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.howToEnter.note3')}
                  </div>
                </div>
              </div>
            ),
          },
          {
            id: 'booth-how-to-exit',
            question: t('faq.workBooth.howToExit.question'),
            answer: (
              <p className="faq__text">{t('faq.workBooth.howToExit.answer')}</p>
            ),
          },
          {
            id: 'booth-lock-not-opening',
            question: t('faq.workBooth.lockNotOpening.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.workBooth.lockNotOpening.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">1.</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.lockNotOpening.step1')}
                  </div>
                </div>
                <div className="l-mt20">
                  <video
                    src={t('faq.workBooth.lockNotOpening.videoSrc')}
                    controls
                    preload="metadata"
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">2.</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.lockNotOpening.step2')}
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">3.</div>
                  <div className="faq-row__body">
                    {t('faq.workBooth.lockNotOpening.step3')}
                  </div>
                </div>
                <div className="l-mt20">
                  <video
                    src={t('faq.workBooth.lockNotOpening.videoSrc2')}
                    controls
                    preload="metadata"
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                </div>
              </div>
            ),
          },
          {
            id: 'booth-reservation-process',
            question: t('faq.reservations.reservationProcess.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.reservations.reservationProcess.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">1.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.step1Main')}
                    </p>
                    <p className="faq__text l-mt10">
                      {t(
                        'faq.reservations.reservationProcess.availabilityDomestic',
                      )}
                    </p>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">2.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.step2')}
                    </p>
                  </div>
                </div>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">3.</div>
                  <div className="faq-row__body">
                    <p className="faq__text">
                      {t('faq.reservations.reservationProcess.boothStep3')}
                    </p>
                  </div>
                </div>
                <p className="faq__text l-mt20">
                  {t('faq.reservations.reservationProcess.entryTitle')}
                </p>
                <p className="faq__text l-mt10">
                  {t('faq.reservations.reservationProcess.entryDomesticDetail')}
                  <br />
                  {t('faq.reservations.reservationProcess.entryDomesticNote')}
                </p>
                <p className="faq__text l-mt20">
                  {t('faq.reservations.reservationProcess.exitTitle')}
                </p>
                <p className="faq__text l-mt10">
                  {t('faq.reservations.reservationProcess.exitDomesticDetail')}
                </p>
              </div>
            ),
          },
        ],
      },
      {
        id: 'others',
        title: t('faq.categories.others'),
        items: [
          {
            id: 'verification-email-others',
            question: t('faq.others.verificationEmail.question'),
            answer: (
              <div>
                <p className="faq__text">
                  {t('faq.others.verificationEmail.intro')}
                </p>
                <div className="faq-row l-mt20">
                  <div className="faq-row__label">-</div>
                  <div className="faq-row__body">
                    {t('faq.others.verificationEmail.reason1')}
                  </div>
                </div>
                <div className="faq-row">
                  <div className="faq-row__label">-</div>
                  <div className="faq-row__body">
                    {t('faq.others.verificationEmail.reason2')}
                  </div>
                </div>
                <div className="faq-row">
                  <div className="faq-row__label">-</div>
                  <div className="faq-row__body">
                    {t('faq.others.verificationEmail.reason3')}
                  </div>
                </div>
                <p className="faq__text l-mt20">
                  {t('faq.others.verificationEmail.solution')}
                </p>
              </div>
            ),
          },
          {
            id: 'forgot-password',
            question: t('faq.others.forgotPassword.question'),
            answer: (
              <p className="faq__text">
                <Link to={ROUTE_PATH.PASSWORD_RESET} className="faq__link">
                  {t('faq.others.forgotPassword.linkText')}
                </Link>
                {t('faq.others.forgotPassword.suffix')}
              </p>
            ),
          },
          {
            id: 'personal-info-others',
            question: t('faq.others.personalInfo.question'),
            answer: (
              <p className="faq__text">{t('faq.others.personalInfo.answer')}</p>
            ),
          },
          {
            id: 'operating-company',
            question: t('faq.others.operatingCompany.question'),
            answer: (
              <p className="faq__text">
                {t('faq.others.operatingCompany.answer')}
              </p>
            ),
          },
          {
            id: 'cancel-account',
            question: t('faq.others.cancelAccount.question'),
            answer: (
              <p className="faq__text">
                {t('faq.others.cancelAccount.answer')}
                <br />
                <a href="mailto:support_work@bitkey.jp" className="faq__link">
                  support_work@bitkey.jp
                </a>
              </p>
            ),
          },
          {
            id: 'contact',
            question: t('faq.others.contact.question'),
            answer: (
              <p className="faq__text">{t('faq.others.contact.answer')}</p>
            ),
          },
        ],
      },
    ],
    [t],
  );

  return { faqCategories };
};
